/**
 * Structured AI execution profile: model + effort for the Overseer or a Worker (#547, #250).
 *
 * One canonical owner action carries a target, an exact offering (runtime + model)
 * and an effort key. There is no universal effort enum: each runtime's capability
 * (reported by the node from its shipped catalog) lists the effort values that
 * runtime can actually apply, or none ("model default").
 *
 * Nothing here builds shell text. The node translates a validated profile into
 * the provider's catalogued launch arguments or in-session commands.
 *
 * Apply rules:
 * - never switch a model mid-turn; a busy target applies after its current task;
 * - keep a healthy PTY: switch in-session when the runtime supports it, otherwise
 *   the change waits for the next safe process boundary and says so;
 * - rotate AI context only when the runtime requires it;
 * - pinned means exact: no silent provider/model fallback;
 * - success is the node-reported actual identity, not the request.
 */

export const AI_EXECUTION_TARGET_KINDS = ['overseer', 'worker'] as const;
export type AiExecutionTargetKind = (typeof AI_EXECUTION_TARGET_KINDS)[number];

export const AI_EXECUTION_APPLY_MODES = ['now', 'after_current_task'] as const;
export type AiExecutionApplyMode = (typeof AI_EXECUTION_APPLY_MODES)[number];

/** Effort keys are catalog words (low, xhigh, max …), never free text. */
export const EFFORT_KEY_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;
/** Registry keys as the DB enforces them (offerings, runtimes). */
export const EXECUTION_REGISTRY_KEY_PATTERN = /^[A-Za-z0-9._:/@-]{1,128}$/;
/** Model identifiers passed to a CLI: no whitespace, quotes or shell metacharacters. */
export const MODEL_KEY_PATTERN = /^[A-Za-z0-9._:/@+-]{1,128}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isEffortKey(value: unknown): value is string {
  return typeof value === 'string' && EFFORT_KEY_PATTERN.test(value);
}

export function isModelKey(value: unknown): value is string {
  return typeof value === 'string' && MODEL_KEY_PATTERN.test(value);
}

// ---------------------------------------------------------------------------
// Runtime capability
// ---------------------------------------------------------------------------

export interface RuntimeExecutionCapability {
  readonly runtimeKey: string;
  /** Effort values this runtime can actually apply. Empty: effort is the model default and not selectable. */
  readonly efforts: readonly string[];
  /** The runtime's own default when no effort is pinned; null when unknown. */
  readonly defaultEffort: string | null;
  /** Whether a live session can change model / effort in place, keeping its PTY. */
  readonly liveModelSwitch: boolean;
  readonly liveEffortSwitch: boolean;
  /** Where the capability came from, e.g. "Claude Code 2.1.281 --effort". Non-secret. */
  readonly evidence: string;
}

export type CapabilityProblem =
  | 'invalid_runtime'
  | 'invalid_effort'
  | 'duplicate_effort'
  | 'default_not_listed'
  | 'invalid_evidence';

export function validateRuntimeCapability(value: RuntimeExecutionCapability): CapabilityProblem | null {
  if (typeof value.runtimeKey !== 'string' || !EXECUTION_REGISTRY_KEY_PATTERN.test(value.runtimeKey)) return 'invalid_runtime';
  if (!Array.isArray(value.efforts) || value.efforts.length > 16 || !value.efforts.every(isEffortKey)) return 'invalid_effort';
  if (new Set(value.efforts).size !== value.efforts.length) return 'duplicate_effort';
  if (value.defaultEffort !== null && !value.efforts.includes(value.defaultEffort)) return 'default_not_listed';
  if (typeof value.evidence !== 'string' || value.evidence.trim() === '' || value.evidence.length > 200) return 'invalid_evidence';
  return null;
}

/** What an owner-facing selector may offer: exactly the runtime's list, or nothing. */
export function effortChoices(capability: RuntimeExecutionCapability | null): {
  readonly configurable: boolean;
  readonly choices: readonly string[];
  readonly reason: string | null;
} {
  if (capability === null) {
    return { configurable: false, choices: [], reason: 'This runtime has not reported which effort values it supports.' };
  }
  if (capability.efforts.length === 0) {
    return { configurable: false, choices: [], reason: 'Effort is the model default; this runtime has no effort control.' };
  }
  return { configurable: true, choices: capability.efforts, reason: null };
}

// ---------------------------------------------------------------------------
// Structured profile request
// ---------------------------------------------------------------------------

export interface AiExecutionProfileRequest {
  readonly orgId: string;
  readonly targetKind: AiExecutionTargetKind;
  /** Worker id for a Worker; the org id for the Overseer (one Main Command per org). */
  readonly targetId: string;
  readonly nodeId: string;
  /** Exact offering (runtime + model); null returns a Worker to Auto. */
  readonly offeringKey: string | null;
  /** A capability-listed effort, or null for the model default. */
  readonly effortKey: string | null;
  readonly applyMode: AiExecutionApplyMode;
}

export type ProfileRequestProblem =
  | 'invalid_org'
  | 'invalid_target_kind'
  | 'invalid_target'
  | 'invalid_node'
  | 'invalid_offering'
  | 'invalid_effort'
  | 'invalid_apply_mode'
  | 'overseer_requires_offering'
  | 'effort_requires_offering'
  | 'unexpected_field';

const REQUEST_KEYS = ['orgId', 'targetKind', 'targetId', 'nodeId', 'offeringKey', 'effortKey', 'applyMode'] as const;

/** Shape check for the one structured owner action. The DB repeats every rule. */
export function validateAiExecutionProfileRequest(value: unknown): ProfileRequestProblem | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return 'unexpected_field';
  const record = value as Record<string, unknown>;
  if (Object.keys(record).some((key) => !(REQUEST_KEYS as readonly string[]).includes(key))) return 'unexpected_field';
  if (typeof record.orgId !== 'string' || !UUID_PATTERN.test(record.orgId)) return 'invalid_org';
  if (!(AI_EXECUTION_TARGET_KINDS as readonly unknown[]).includes(record.targetKind)) return 'invalid_target_kind';
  if (typeof record.targetId !== 'string' || !UUID_PATTERN.test(record.targetId)) return 'invalid_target';
  if (record.targetKind === 'overseer' && record.targetId !== record.orgId) return 'invalid_target';
  if (typeof record.nodeId !== 'string' || !UUID_PATTERN.test(record.nodeId)) return 'invalid_node';
  if (record.offeringKey !== null && (typeof record.offeringKey !== 'string' || !EXECUTION_REGISTRY_KEY_PATTERN.test(record.offeringKey))) {
    return 'invalid_offering';
  }
  if (record.effortKey !== null && !isEffortKey(record.effortKey)) return 'invalid_effort';
  if (!(AI_EXECUTION_APPLY_MODES as readonly unknown[]).includes(record.applyMode)) return 'invalid_apply_mode';
  if (record.targetKind === 'overseer' && record.offeringKey === null) return 'overseer_requires_offering';
  if (record.offeringKey === null && record.effortKey !== null) return 'effort_requires_offering';
  return null;
}

/** An effort is acceptable only when the runtime's own capability lists it. */
export function checkEffortSupported(
  effortKey: string | null,
  capability: RuntimeExecutionCapability | null,
): { readonly ok: true } | { readonly ok: false; readonly code: 'unsupported_effort' | 'effort_capability_unknown'; readonly message: string } {
  if (effortKey === null) return { ok: true };
  if (capability === null) {
    return { ok: false, code: 'effort_capability_unknown', message: 'The node has not reported effort support for this runtime.' };
  }
  if (!capability.efforts.includes(effortKey)) {
    return {
      ok: false,
      code: 'unsupported_effort',
      message: capability.efforts.length === 0
        ? `${capability.runtimeKey} has no effort control; its effort is the model default.`
        : `${capability.runtimeKey} supports ${capability.efforts.join(', ')}; not ${effortKey}.`,
    };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Apply planning (pure): when and how a Worker change takes effect
// ---------------------------------------------------------------------------

export interface ExecutionIdentity {
  readonly runtimeKey: string | null;
  readonly modelKey: string | null;
  readonly effortKey: string | null;
}

export type WorkerActivity = 'idle' | 'busy' | 'no_live_session';

export type ProfileApplyMethod =
  /** Nothing changes for the AI process. */
  | 'no_change'
  /** Catalogued in-session commands; the PTY and conversation stay. */
  | 'in_session_switch'
  /** The runtime only reads model/effort at launch; applied when its process next starts. */
  | 'next_process_start'
  /** A different runtime is a different program: it starts at the next safe boundary. */
  | 'runtime_change';

export interface ProfileApplyPlan {
  readonly outcome: 'apply_now' | 'deferred' | 'refused';
  readonly method: ProfileApplyMethod;
  /** True when the Worker's existing terminal is kept. */
  readonly ptyPreserved: boolean;
  /** True only when the AI conversation must be rotated/freshened. */
  readonly rotatesContext: boolean;
  readonly reason: string;
}

export function planWorkerProfileApply(input: {
  readonly activity: WorkerActivity;
  readonly applyMode: AiExecutionApplyMode;
  readonly current: ExecutionIdentity;
  readonly requested: ExecutionIdentity;
  readonly capability: RuntimeExecutionCapability | null;
}): ProfileApplyPlan {
  const { current, requested, capability } = input;
  const runtimeChanged = current.runtimeKey !== null && current.runtimeKey !== requested.runtimeKey;
  const modelChanged = current.modelKey !== requested.modelKey;
  const effortChanged = current.effortKey !== requested.effortKey;

  if (input.activity === 'busy' && input.applyMode === 'now') {
    return {
      outcome: 'refused', method: 'no_change', ptyPreserved: true, rotatesContext: false,
      reason: 'The Worker is busy. A model or effort never changes mid-turn: apply after the current task, or stop it with the existing stop control first.',
    };
  }
  if (!runtimeChanged && !modelChanged && !effortChanged) {
    return { outcome: 'apply_now', method: 'no_change', ptyPreserved: true, rotatesContext: false, reason: 'Already running this profile.' };
  }
  const deferOrNow = input.activity === 'busy' ? 'deferred' : 'apply_now';
  if (input.activity === 'no_live_session') {
    return {
      outcome: 'apply_now', method: 'next_process_start', ptyPreserved: true, rotatesContext: false,
      reason: 'No live AI session: the profile is used when the Worker next starts its AI process.',
    };
  }
  if (runtimeChanged) {
    return {
      outcome: 'deferred', method: 'runtime_change', ptyPreserved: false, rotatesContext: true,
      reason: 'A different runtime is a different program; it starts at the next safe task boundary once the current result is durable.',
    };
  }
  const liveOk = capability !== null
    && (!modelChanged || capability.liveModelSwitch)
    && (!effortChanged || capability.liveEffortSwitch);
  if (liveOk) {
    return {
      outcome: deferOrNow, method: 'in_session_switch', ptyPreserved: true, rotatesContext: false,
      reason: input.activity === 'busy'
        ? 'Switches in the same session after the current task; the terminal and conversation stay.'
        : 'Switches in the same session now; the terminal and conversation stay.',
    };
  }
  return {
    outcome: 'deferred', method: 'next_process_start', ptyPreserved: false, rotatesContext: true,
    reason: 'This runtime reads model/effort only when its process starts. Brunel does not restart the healthy terminal now; '
      + 'the change takes effect at the next task boundary, which starts a new AI process once the current result is durable.',
  };
}

// ---------------------------------------------------------------------------
// Configured vs actual
// ---------------------------------------------------------------------------

export interface ExecutionComparison {
  readonly status: 'match' | 'mismatch' | 'unknown' | 'pending';
  readonly differences: readonly ('runtime' | 'model' | 'effort')[];
}

/**
 * Never claims success from the request alone. Actual identity must be reported
 * by the node; an unreported actual is Unknown, an older one is Pending.
 */
export function compareConfiguredActual(input: {
  readonly configured: ExecutionIdentity;
  readonly actual: ExecutionIdentity | null;
  readonly configuredAt: string | null;
  readonly actualAt: string | null;
}): ExecutionComparison {
  if (input.actual === null || input.actualAt === null) return { status: 'unknown', differences: [] };
  if (input.configuredAt !== null && Date.parse(input.actualAt) < Date.parse(input.configuredAt)) {
    return { status: 'pending', differences: [] };
  }
  const differences: ('runtime' | 'model' | 'effort')[] = [];
  if (input.configured.runtimeKey !== input.actual.runtimeKey) differences.push('runtime');
  if (input.configured.modelKey !== input.actual.modelKey) differences.push('model');
  // A null configured effort means "model default": any reported effort is acceptable.
  if (input.configured.effortKey !== null && input.configured.effortKey !== input.actual.effortKey) differences.push('effort');
  return { status: differences.length === 0 ? 'match' : 'mismatch', differences };
}

// ---------------------------------------------------------------------------
// Assigned vs actual: runtime alias and resolved model kept apart
// ---------------------------------------------------------------------------

/** One registered offering's runtime and model (brunel.model_offerings). */
export interface OfferingModelRule {
  readonly runtimeKey: string;
  readonly modelKey: string;
}

export interface ModelIdentitySplit {
  /**
   * The runtime alias a model key names: a model key equal to its runtime key is how an
   * offering leaves the model to the runtime ("<runtime>/<runtime>"). Null otherwise.
   */
  readonly runtimeAlias: string | null;
  /** The concrete model execution evidence names; null when not proven. */
  readonly resolvedModel: string | null;
}

/** Splits one runtime/model record into its runtime alias and its resolved model. */
export function splitModelIdentity(runtimeKey: string | null, modelKey: string | null): ModelIdentitySplit {
  const model = modelKey?.trim() || null;
  if (model === null) return { runtimeAlias: null, resolvedModel: null };
  if (runtimeKey !== null && model === runtimeKey.trim()) return { runtimeAlias: model, resolvedModel: null };
  return { runtimeAlias: null, resolvedModel: model };
}

export interface AssignedActualAssessment {
  /**
   * differs: the actual runtime is not the assigned one, or the resolved model is outside what
   * the assigned offering allows. match: both proven within it. not_proven: evidence is missing.
   */
  readonly status: 'match' | 'differs' | 'not_proven';
  readonly differences: readonly ('runtime' | 'model_not_allowed')[];
  readonly actual: ModelIdentitySplit;
}

/**
 * Whether what ran is what was assigned. An assigned runtime alias allows whatever model that
 * runtime resolves to, except a model the offering configuration registers only under another
 * runtime; an assigned concrete model allows only itself. Missing evidence is never a match
 * or a mismatch.
 */
export function assessAssignedActual(input: {
  readonly assigned: { readonly runtimeKey: string | null; readonly modelKey: string | null } | null;
  readonly actual: { readonly runtimeKey: string | null; readonly modelKey: string | null } | null;
  readonly offerings?: readonly OfferingModelRule[];
}): AssignedActualAssessment {
  const assignedRuntime = input.assigned?.runtimeKey?.trim() || null;
  const actualRuntime = input.actual?.runtimeKey?.trim() || null;
  const actual = splitModelIdentity(actualRuntime, input.actual?.modelKey ?? null);
  if (assignedRuntime === null || actualRuntime === null) return { status: 'not_proven', differences: [], actual };
  const differences: ('runtime' | 'model_not_allowed')[] = [];
  if (assignedRuntime !== actualRuntime) differences.push('runtime');
  const resolved = actual.resolvedModel;
  if (resolved !== null) {
    const assigned = splitModelIdentity(assignedRuntime, input.assigned?.modelKey ?? null);
    const offerings = input.offerings ?? [];
    const allowed = assigned.resolvedModel !== null
      ? resolved === assigned.resolvedModel
      : offerings.some((rule) => rule.modelKey === resolved && rule.runtimeKey === assignedRuntime)
        || !offerings.some((rule) => rule.modelKey === resolved);
    if (!allowed) differences.push('model_not_allowed');
  }
  if (differences.length > 0) return { status: 'differs', differences, actual };
  return { status: resolved === null ? 'not_proven' : 'match', differences, actual };
}

// ---------------------------------------------------------------------------
// Audit shape: previous / requested / actual / apply mode / failure; nothing secret
// ---------------------------------------------------------------------------

export interface ProfileAuditPayload {
  readonly targetKind: AiExecutionTargetKind;
  readonly targetId: string;
  readonly nodeId: string;
  readonly previous: { readonly offeringKey: string | null; readonly effortKey: string | null };
  readonly requested: { readonly offeringKey: string | null; readonly effortKey: string | null };
  readonly actual: ExecutionIdentity | null;
  readonly applyMode: AiExecutionApplyMode;
  readonly outcome: string;
  readonly failure: string | null;
}

export function profileAuditPayload(input: ProfileAuditPayload): ProfileAuditPayload {
  const clean = (value: string | null) => (value === null ? null : value.slice(0, 128));
  return {
    targetKind: input.targetKind,
    targetId: input.targetId,
    nodeId: input.nodeId,
    previous: { offeringKey: clean(input.previous.offeringKey), effortKey: clean(input.previous.effortKey) },
    requested: { offeringKey: clean(input.requested.offeringKey), effortKey: clean(input.requested.effortKey) },
    actual: input.actual,
    applyMode: input.applyMode,
    outcome: input.outcome.slice(0, 64),
    failure: input.failure === null ? null : input.failure.slice(0, 300),
  };
}
